import React from 'react';
import parsePhoneNumberFromString from 'libphonenumber-js';

import { useSelector } from 'react-redux';
import { EActionType } from 'components/activity/enums';
import { useAuth } from 'lib/useAuth';
import { ApplicationState } from 'store/store';
import { EmailAuthFlow } from './EmailAuthFlow';
import { PhoneAuthFlow } from './PhoneAuthFlow';
import { useSummaryGuestConversion } from './hooks/useSummaryGuestConversion';

interface SummaryGuestConversionSectionProps {
    orderId: number;
    onAuthComplete?: () => void;
    title: string;
    subtitle?: string;
    footer: string;
}

export const SummaryGuestConversionSection: React.FC<SummaryGuestConversionSectionProps> = ({
    orderId,
    onAuthComplete,
    title,
    subtitle,
    footer
}) => {
    const { user, isGuest } = useAuth();

    const { identity: guestIdentity } = useSelector((state: ApplicationState) => state.guest);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const isEmailNotificationsEnabled =
        !!settings?.actionNotifications &&
        !!(
            settings.actionNotifications[EActionType.ORDER_USER_BILLED]?.email ||
            settings.actionNotifications[EActionType.ORDER_COMPLETED]?.email
        );

    const {
        loading,
        submittedEmail,
        loginIdentity,
        submit,
        authStage,
        setAuthStage,
        handleSignupSuccess,
        handleAuthComplete,
        handleForgotPassword,
        handleTwoFactor,
        twoFactorIdentity,
        isPhoneAuth
    } = useSummaryGuestConversion({ orderId, onAuthComplete });

    const phoneNumber = user?.phoneNumberLastUsedToOrder;
    const normalizedPhoneNumber = React.useMemo(() => {
        if (!phoneNumber) {
            return undefined;
        }
        try {
            const parsed = parsePhoneNumberFromString(phoneNumber);
            return parsed?.nationalNumber.toString() ?? phoneNumber;
        } catch {
            return phoneNumber;
        }
    }, [phoneNumber]);

    const inlineDefaultIdentity = isPhoneAuth ? normalizedPhoneNumber : submittedEmail;
    const inlinePreferIdentityType: 'email' | 'phone' = isPhoneAuth ? 'phone' : 'email';
    const inlineDefaultTab: 'email' | 'phone' = isPhoneAuth ? 'phone' : 'email';

    // When the account was found via the phone probe, use the raw E.164 phone as
    // defaultIdentity. LoginForm will normalise it (strip the country code) only when a
    // country picker is rendered alongside the field — leaving the full number intact in
    // single-field mode where there is no picker.
    const loginDefaultIdentity = loginIdentity ?? inlineDefaultIdentity;
    // Activate the phone tab when the matched identity is a phone number.
    const loginDefaultTab: 'email' | 'phone' = React.useMemo(() => {
        if (!loginIdentity) {
            return inlineDefaultTab;
        }
        try {
            return parsePhoneNumberFromString(loginIdentity) ? 'phone' : 'email';
        } catch {
            return inlineDefaultTab;
        }
    }, [inlineDefaultTab, loginIdentity]);

    const defaultEmail = React.useMemo(
        () => user?.email || (isGuest ? guestIdentity : undefined) || '',
        [guestIdentity, isGuest, user?.email]
    );

    const handleSubmit = React.useCallback(({ email }: { email: string }) => submit(email), [submit]);
    const handleSwitchToSignUp = React.useCallback(() => setAuthStage('signup'), [setAuthStage]);
    const handleSwitchToLogIn = React.useCallback(() => setAuthStage('login'), [setAuthStage]);
    const handleSwitchToForgotPassword = React.useCallback(
        () => handleForgotPassword(),
        [handleForgotPassword]
    );
    const handleSwitchToTwoFactor = React.useCallback(
        (identity: string) => handleTwoFactor(identity),
        [handleTwoFactor]
    );

    const effectiveAuthStage = !isEmailNotificationsEnabled && authStage === 'idle' ? 'signup' : authStage;

    const isAuthStageActive =
        effectiveAuthStage === 'login' ||
        effectiveAuthStage === 'signup' ||
        effectiveAuthStage === 'activate' ||
        effectiveAuthStage === 'forgot-password' ||
        effectiveAuthStage === 'two-factor';

    const authFormsProps = {
        authStage: effectiveAuthStage,
        loginDefaultIdentity,
        loginDefaultTab,
        defaultPhone: phoneNumber,
        inlineDefaultIdentity,
        preferIdentityType: inlinePreferIdentityType,
        twoFactorIdentity,
        onAuthComplete: handleAuthComplete,
        onSwitchToSignUp: handleSwitchToSignUp,
        onSwitchToLogIn: handleSwitchToLogIn,
        onSwitchToForgotPassword: handleSwitchToForgotPassword,
        onSwitchToTwoFactor: handleSwitchToTwoFactor,
        onSignupSuccess: handleSignupSuccess
    };

    if (isPhoneAuth) {
        return (
            <PhoneAuthFlow
                isAuthStageActive={isAuthStageActive}
                title={title}
                subtitle={subtitle}
                authFormsProps={authFormsProps}
            />
        );
    }

    return (
        <EmailAuthFlow
            title={title}
            subtitle={subtitle}
            footer={footer}
            defaultEmail={defaultEmail}
            isLoading={loading}
            isAuthStageActive={isAuthStageActive}
            onSubmit={handleSubmit}
            authFormsProps={authFormsProps}
        />
    );
};
